'use strict';

import {keys} from 'ts-transformer-keys';
import _ from 'lodash';

import metaModel from '~/shared/src/utilities/metaModel';
import PropertySet from '~/shared/src/mm/PropertySet';
import Item from '~/shared/src/ci/Item';
import Property, {PropertyAttributes} from '~/shared/src/ci/Property';
import EnumProperty from '~/shared/src/ci/EnumProperty';
import {renderValue} from '~/shared/src/utilities/valueRenderers';
import {FilterValueType} from '~/shared/src/validationSchemes/modules/overview';

import dbDefinitionMapper from '~/backend/src/connectors/db/dbDefinitionMapper';


const allowedAttributes = keys<PropertyAttributes>() as string[];

class PropertyTemplateStringResolver {
    rawMode : boolean = false;

    protected _item : Item;

    constructor( item : Item ) {
        this._item = item;

        return new Proxy( this, this );
    }

    set( target : PropertyTemplateStringResolver, attribute : string, value : any ) {
        let retVal = false;

        if ( attribute === 'rawMode' ) {
            retVal = true;
            this.rawMode = value;
        }

        return retVal;
    }

    get( target : PropertyTemplateStringResolver, propertyTypeName : string ) {
        const propertyTypeId = metaModel[ this._item.schema ].getPropertyType( _.upperFirst( propertyTypeName ) )?.id ?? 0;

        let retVal : any;

        if ( propertyTypeId ) {
            if ( this._item.properties.has( propertyTypeId ) ) {
                retVal = new Proxy( this._item.properties.get( propertyTypeId )!, { get: this.getByProperty.bind( this ) } );
            }
            else if ( this._item.enumProperties.has( propertyTypeId ) ) {
                retVal = new Proxy( this._item.enumProperties.get( propertyTypeId )!, { get: this.getByEnumProperty.bind( this ) } );
            }
            else {
                const propertySet = metaModel[ this._item.schema ].getPropertySetForItemTypeAndPropertyType( this._item.itemTypeId, propertyTypeId );

                if ( propertySet ) {
                    retVal = new Proxy( propertySet, { get: this.getByPropertySet.bind( this ) } );
                }
            }
        }

        if ( !retVal ) {
            retVal = new Proxy( { name: `property.${propertyTypeName}` }, { get: this.getByNone.bind( this ) } );
        }

        return retVal;
    }

    getByProperty( property : Property, attribute : Symbol, propertyProxy : Property ) : ( () => string );
    getByProperty( property : Property, attribute : string, propertyProxy : Property ) : string;
    getByProperty( property : Property, attribute : string | Symbol, propertyProxy : Property ) : string | ( () => string ) {
        let retVal : string | ( () => string ) = '';

        if ( typeof attribute === 'symbol' ) {
            if ( attribute === Symbol.toPrimitive ) {
                retVal = () => {
                    return propertyProxy.value as string;
                };
            }
        }
        else {
            retVal = attribute as string;

            attribute = _.lowerFirst( attribute as string );

            if ( allowedAttributes.includes( attribute ) ) {
                const rawValue = property[ attribute as keyof PropertyAttributes ];

                if ( rawValue !== undefined && rawValue !== null && rawValue !== '' ) {
                    if ( attribute === 'value' ) {
                        const propertySet  = property.propertySet,
                              valueType    = propertySet.valueType,
                              params : any = {
                                  valueUnit: propertySet.valueUnit
                              };

                        let rawMode : boolean = this.rawMode;

                        if ( valueType === 'BOOLEAN' || valueType === 'COLOR' || valueType === 'COORDINATE' || valueType === 'EMAIL' || valueType === 'IMAGEDATA' || valueType === 'JSON' || valueType === 'LINK' || valueType === 'TEXT' ) {
                            rawMode = true;
                        }

                        retVal = renderValue( valueType, rawValue, params, rawMode ) as string;
                    }
                    else {
                        retVal = renderValue( dbDefinitionMapper.schemas[ property.schema ].views[ 'property' ].getFieldType( attribute ) as FilterValueType, rawValue, undefined, this.rawMode ) as string;
                    }
                }
                else {
                    retVal = '';
                }
            }
            else if ( attribute === 'valueLabel' ) {
                retVal = property.valueLabel ?? '';
            }
            else if ( attribute === 'propertyTypeId' || attribute === 'typeId' ) {
                retVal = String( property.propertySet.propertyTypeId );
            }
            else if (
                attribute === 'propertySetName' || attribute === 'setName'
                || attribute === 'propertyTypeName' || attribute === 'typeName'
            ) {
                retVal = !this.rawMode ? property.propertySet.label : property.propertySet.name;
            }
            else if ( attribute === 'statusName' ) {
                retVal = !this.rawMode ? property.status.label : property.status.name;
            }
        }

        if ( !retVal ) {
            retVal = `property.${property.propertySet.name}.${String( attribute )}`;
        }

        return retVal;
    }

    getByEnumProperty( enumProperty : EnumProperty, key : Symbol, enumPropertyProxy : EnumProperty ) : ( () => string ) | undefined;
    getByEnumProperty( enumProperty : EnumProperty, key : string, enumPropertyProxy : EnumProperty ) : Property[] | undefined;
    getByEnumProperty( enumProperty : EnumProperty, key : string, enumPropertyProxy : EnumProperty ) : Property | undefined;
    getByEnumProperty( enumProperty : EnumProperty, key : string | Symbol, enumPropertyProxy : EnumProperty ) : Property | Property[] | ( () => string ) | undefined {
        let retVal : ( () => string ) | Property | Property[] | undefined;

        if ( typeof key === 'symbol' ) {
            if ( key === Symbol.toPrimitive ) {
                retVal = () => {
                    const properties : Property[]  = [ ...enumProperty.properties.values() ],
                          keyValuePairs : string[] = [];

                    properties.sort( ( property1, property2 ) => {
                        if ( property1.key && property2.key ) {
                            if ( property1.key > property2.key ) {
                                return 1;
                            }
                            else if ( property1.key < property2.key ) {
                                return -1;
                            }
                        }

                        return 0;
                    } );

                    for ( let i = 0; i < properties.length; i++ ) {
                        keyValuePairs.push( `${properties[ i ].stringKey}: ${properties[ i ].value}` );
                    }

                    return keyValuePairs.join( '; ' );
                };
            }
        }
        else if ( typeof key === 'string' ) {

            // get property by stringKey
            if ( enumProperty.properties.has( key ) ) {
                retVal = new Proxy( enumProperty.properties.get( key )!, { get: this.getByProperty.bind( this ) } );
            }

            // get property by index
            else if ( String( Number( key ) ) === key ) {
                const properties : Property[] = [ ...enumProperty.properties.values() ];

                properties.sort( ( property1, property2 ) => {
                    if ( property1.key && property2.key ) {
                        if ( property1.key > property2.key ) {
                            return 1;
                        }
                        else if ( property1.key < property2.key ) {
                            return -1;
                        }
                    }

                    return 0;
                } );

                if ( properties[ Number( key ) ] ) {
                    retVal = new Proxy( properties[ Number( key ) ], { get: this.getByProperty.bind( this ) } );
                }
            }

            // get properties as array by key
            else {
                const properties : Property[] = [ ...enumProperty.properties.values() ];

                retVal = [];

                properties.sort( ( property1, property2 ) => {
                    if ( property1.key && property2.key ) {
                        if ( property1.key > property2.key ) {
                            return 1;
                        }
                        else if ( property1.key < property2.key ) {
                            return -1;
                        }
                    }

                    return 0;
                } );

                for ( let i = 0; i < properties.length; i++ ) {
                    if ( properties[ i ].key === key ) {
                        ( retVal as Property[] ).push( new Proxy( properties[ i ], { get: this.getByProperty.bind( this ) } ) );
                    }
                }

                if ( !retVal.length ) {
                    retVal = undefined;
                }
            }
        }

        return retVal;
    }

    getByPropertySet( propertySet : PropertySet, attribute : Symbol ) : ( () => string );
    getByPropertySet( propertySet : PropertySet, attribute : string ) : string;
    getByPropertySet( propertySet : PropertySet, attribute : string | Symbol ) : string | ( () => string ) {
        let retVal : string | ( () => string ) = '';

        if ( typeof attribute === 'symbol' ) {
            if ( attribute === Symbol.toPrimitive ) {
                retVal = () => {
                    return '';
                };
            }
        }
        else {
            retVal = attribute as string;

            attribute = _.lowerFirst( attribute as string );

            if ( allowedAttributes.includes( attribute ) ) {
                retVal = '';
            }
            else if ( attribute === 'propertyTypeId' || attribute === 'typeId' ) {
                retVal = String( propertySet.propertyTypeId );
            }
            else if ( attribute === 'propertyTypeName' || attribute === 'typeName' ) {
                retVal = !this.rawMode ? propertySet.label : propertySet.name;
            }
            else if ( attribute === 'statusName' ) {
                retVal = '';
            }
        }

        if ( !retVal ) {
            retVal = `property.${propertySet.name}.${String( attribute )}`;
        }

        return retVal;
    }

    getByNone( none : { name : string }, attribute : Symbol ) : ( () => string );
    getByNone( none : { name : string }, attribute : string ) : string;
    getByNone( none : { name : string }, attribute : string | Symbol ) : string | ( () => string ) {
        let retVal : string | ( () => string ) = '';

        if ( typeof attribute === 'symbol' ) {
            if ( attribute === Symbol.toPrimitive ) {
                retVal = () => {
                    return none.name;
                };
            }
        }
        else {
            retVal = `${none.name}.${attribute}`;
        }

        return retVal;
    }
}

export default PropertyTemplateStringResolver;
